import React from 'react';
import { useAppDispatch } from 'bootstrap/hooks';
import { pushWithSearchQuery } from 'utils';
import { Product } from 'bootstrap/data/product/Product';
import { FeatureSection } from 'subscription/components/featuresection/FeatureIn';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { useFeatures } from 'useFeatures';
import { useSingleProductLogic } from 'subscription/singleproduct/SingleProductPayment.hook';
import { PlanSelectType } from 'subscription/singleproduct/components/plansection/PlanSection.constants';
import { PlanSectionWrapper } from './components/plansectionwrapper/PlanSectionWrapper';
import { CardSecure } from './components/cardsecure/CardSecure';
import { PaymentsBrand } from './components/paymentsbrand/PaymentsBrand';
import { PlanSection } from './components/plansection/PlanSection';
import styles from './styles.module.scss';

type Props = {
  defaultProduct: Product;
  products: Map<string, Product>;
  paymentFormRef?: React.RefObject<HTMLDivElement>;
};

export const SingleProductPayment = ({
  defaultProduct,
  products,
  paymentFormRef,
}: Props) => {
  const dispatch = useAppDispatch();

  const { isFeaturedInFlow } = useFeatures();

  const {
    authToken,
    currencyCode,
    product,
    weekProduct,
    monthProduct,
    quarterProduct,
    planType,
    handleGetPlanClick,
  } = useSingleProductLogic({
    defaultProduct,
    products,
    paymentFormRef,
  });

  if (!authToken) {
    dispatch(pushWithSearchQuery('/wellness-create-account'));
    return null;
  }

  const getProductByPlanType = (planType?: PlanSelectType) => {
    switch (planType) {
      case PlanSelectType.WEEK:
        return weekProduct;
      case PlanSelectType.QUARTER:
        return quarterProduct;
      case PlanSelectType.MONTH:
      default:
        return monthProduct;
    }
  };

  const getMonthPrice = (planType?: PlanSelectType) => {
    return getProductByPlanType(planType).getFormattedRenewsPrice(currencyCode);
  };

  const getFirstMonthPrice = (planType?: PlanSelectType) => {
    const product = getProductByPlanType(planType);
    return planType === PlanSelectType.WEEK
      ? product.getFormattedWeekPrice(currencyCode)
      : product.getFormattedPrice(currencyCode);
  };

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.REACT_APP_RECAPTCHA_KEY || ''}
      scriptProps={{
        async: false,
        defer: false,
        appendTo: 'head',
        nonce: undefined,
      }}
    >
      <div className={styles.payment}>
        <div
          className={`
          ${styles.paymentContainer} 
          ${styles.bottom90}
        `}
        >
          <h3>Your personalized plan is ready!</h3>
          {isFeaturedInFlow && <FeatureSection />}
          <PlanSectionWrapper
            handleGetPlanClick={handleGetPlanClick}
            product={product}
          >
            <PlanSection
              type={PlanSelectType.WEEK}
              isManualPrice={true}
              isManualTerm={true}
              currencyCode={currencyCode}
              product={weekProduct}
              isRenewPriceVisible={true}
              isSelected={planType === PlanSelectType.WEEK}
            />
            <PlanSection
              type={PlanSelectType.MONTH}
              currencyCode={currencyCode}
              isRenewPriceVisible={true}
              product={monthProduct}
              isSelected={planType === PlanSelectType.MONTH}
              label="most popular"
            />

            <PlanSection
              type={PlanSelectType.QUARTER}
              currencyCode={currencyCode}
              isRenewPriceVisible={true}
              product={quarterProduct}
              isSelected={planType === PlanSelectType.QUARTER}
            />
          </PlanSectionWrapper>

          <div className={styles.note}>
            By clicking "Get My Plan", you agree to automatic subscription
            renewal. First{' '}
            {planType === PlanSelectType.WEEK
              ? 'week'
              : product.getRenewPeriodName()}{' '}
            is {getFirstMonthPrice(planType)}, then {getMonthPrice(planType)}/
            {product.getRenewPeriodName()}. Cancel via the app or email:
            hello@loona.app. See{' '}
            <a href="https://loona.app/terms.html">Terms of Use</a> &{' '}
            <a href="https://loona.app/privacy.html">Privacy Policy</a> for
            details.
          </div>

          <CardSecure />
          <PaymentsBrand />
        </div>
      </div>
    </GoogleReCaptchaProvider>
  );
};
